'use client';

import * as React from 'react';

import { cn } from '@workspace/ui/lib/utils';

import {
  testimonials as allTestimonials,
  type Testimonial
} from '../testimonials';
import { TestimonialCard } from './testimonial-card';

export type TestimonialsWallProps = {
  /** Defaults to every review, in source order. */
  testimonials?: Testimonial[];
  title?: string;
  description?: string;
  className?: string;
};

/**
 * Masonry wall of customer reviews.
 *
 * CSS columns rather than a grid: cards are different heights because the
 * quotes are different lengths, and columns let them stagger the way a wall of
 * love should instead of leaving ragged gaps under short cards. `break-inside-avoid`
 * is what stops a card being split across a column boundary.
 */
export function TestimonialsWall({
  testimonials: items = allTestimonials,
  title,
  description,
  className
}: TestimonialsWallProps): React.JSX.Element {
  return (
    <div className={cn('space-y-8', className)}>
      {(title || description) && (
        <div className="mx-auto max-w-xl text-center">
          {title && (
            <h2 className="text-2xl font-semibold tracking-tight">{title}</h2>
          )}
          {description && (
            <p className="mt-2 text-sm text-muted-foreground">{description}</p>
          )}
        </div>
      )}

      <div className="gap-4 sm:columns-2 lg:columns-3 xl:columns-4">
        {items.map((testimonial) => (
          <TestimonialCard
            key={testimonial.name}
            testimonial={testimonial}
            layout="start"
            className="mb-4 break-inside-avoid"
          />
        ))}
      </div>
    </div>
  );
}
